<template>
  <aw-form v-model="v$" @success="onDeliveryDataSaved">
    <div :class="$style.inputList">
      <div
        v-for="{ key, field, error } in [
          ...(isGuestCheckout ? [EMAIL, CONFIRM_EMAIL] : []),
          isRev ? LAST_NAME : FIRST_NAME,
          isRev ? FIRST_NAME : LAST_NAME,
          CONTACT_PHONE,
          TAKEOVER_POINT,
          COMMENT
        ].map(key => ({ key, ...validators[key]}))"
        :key="key"
        :class="$style.inputListItem"
      >
        <label
          :for="`${componentId}_${key}`"
          :class="[$style.inputListItemLabel, {[$style.inputListItemLabelComment]: key === COMMENT}]"
          v-text="labels[key]"
        />
        <aw-select-version2
          v-if="key === TAKEOVER_POINT"
          v-bind="field"
          :class="[$style.inputListItemInput, $style.inputListItemInputSelect]"
          :options="departmentStores"
          :inline-label-text="floatingLabels[key] || ''"
          option-id-property="id"
          option-label-property="name"
          :multiple="false"
          :dropdown-icon-attrs="{
            name: 'arrow-down-16',
            size: 16
          }"
        />
        <aw-textarea-version2
          v-else-if="key === COMMENT"
          :class="$style.inputListItemInput"
          v-bind="field"
          :widget-attrs="{ maxLength: TEXTAREA_MAX_LENGTH }"
          has-label
          :floating-label-text="floatingLabels[key]"
          need-countdown
        />
        <div
          v-else
          :class="[
            $style.inputListItemInput,
            {
              [$style.inputListItemInputMedium]: [FIRST_NAME, LAST_NAME].includes(key),
              [$style.inputListItemInputLong]: [EMAIL, CONFIRM_EMAIL].includes(key),
              [$style.inputListItemInputPhone]: [CONTACT_PHONE].includes(key),
            }]"
        >
          <aw-input-version2
            v-bind="field"
            :has-label="Boolean(floatingLabels[key])"
            :floating-label-text="floatingLabels[key] || ''"
            :widget-attrs="{ id: `${componentId}_${key}` }"
          >
            <template v-if="prefixes[key]" #prefix>
              {{ prefixes[key] }}
            </template>
          </aw-input-version2>
        </div>
        <div :class="$style.inputListItemHelp" v-text="helpTexts[key]" />
        <aw-minimal-error :class="$style.inputListItemError" v-bind="error" />
      </div>
    </div>
    <aw-separator />
    <lv-button
      :type="v$.$invalid ? 'button' : 'submit'"
      :disabled="v$.$invalid"
      :loading="isLoading"
      styling="primary-dark"
      :shrink="!isButtonStretch"
      :stretch="isButtonStretch"
    >
      {{ $awt('aw.checkout.delivery_data_form.save_button') }}
    </lv-button>
  </aw-form>
</template>

<script setup>
  import AwForm from '~~/common/components/Form/AwForm.vue';
  import { useVuelidate } from '@vuelidate/core';
  import { awRequired, awEmail, phonePrefix, awValidOptionalMobile } from '~~/common/utils/form.js';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import { useRuntimeConfig, useNuxtApp, useId } from 'nuxt/app';
  import AwInputVersion2, { awInputV2Props } from '~~/common/components/Common/AwInputVersion2.vue';
  import AwMinimalError from '~~/common/components/Common/AwMinimalError.vue';
  import { computed, inject, onMounted, ref } from 'vue';
  import { camelToSnakeCase } from '~~/common/utils/index.js';
  import AwSelectVersion2, { awSelectV2Props } from '~~/common/components/Common/AwSelectVersion2.vue';
  import AwTextareaVersion2 from '~~/common/components/Common/AwTextareaVersion2.vue';
  import { LvButton } from '~~/common/components/loginet-vue-shop/index.mjs';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import { useNameOrderConfig } from '~~/shop/config/NameOrderConfig.js';
  import { useUserStore } from '~~/common/stores/user.js';
  import { useCheckoutStore } from '~~/common/stores/checkout.js';
  import { useDeliveryStore } from '~/stores/delivery.js';

  const TEXTAREA_MAX_LENGTH = 400;

  const userInterfaceStore = useUserInterfaceStore();

  const props = defineProps({
    formData: {
      type: Object,
      default: null,
    },
    isGuestCheckout: {
      type: Boolean,
      default: true,
    },
    selectedDeliveryMethod: {
      type: Object,
      required: true,
    },
    departmentStores: {
      type: Array,
      required: true,
    },
  });

  const emits = defineEmits(['take-over-data-saved']);

  const { isRev } = useNameOrderConfig();

  const EMAIL = 'email';
  const CONFIRM_EMAIL = 'confirmEmail';
  const FIRST_NAME = 'firstName';
  const LAST_NAME = 'lastName';
  const CONTACT_PHONE = 'contactPhone';
  const TAKEOVER_POINT = 'takeOverPoint';
  const COMMENT = 'comment';

  const componentId = useId();
  const screenRange = computed(() => userInterfaceStore.mediaQueries);
  const {
    $awt,
    $logger,
  } = useNuxtApp();
  const isLoading = ref(false);

  const currentCheckoutType = inject('currentCheckoutType');
  const currentCheckout = inject('currentCheckout');

  const models = {
    [FIRST_NAME]: ref(''),
    [LAST_NAME]: ref(''),
    [EMAIL]: ref(''),
    [CONFIRM_EMAIL]: ref(''),
    [CONTACT_PHONE]: ref(''),
    [TAKEOVER_POINT]: ref(''),
    [COMMENT]: ref(''),
  };

  const config = useRuntimeConfig();
  const phoneValidator = (config.public.isDeliveryPhoneAllowed
    ? {
      required: awRequired(),
      mobile: awValidOptionalMobile(),
    }
    : {}
  );

  const v$ = useVuelidate({
    [FIRST_NAME]: {
      required: awRequired(),
      ucbAddressValidator (value) {
        return Boolean(userInterfaceStore.ucbAddressValidator(value));
      },
    },
    [LAST_NAME]: {
      required: awRequired(),
      ucbAddressValidator (value) {
        return Boolean(userInterfaceStore.ucbAddressValidator(value));
      },
    },
    [EMAIL]: {
      required: awRequired({
        ifFun () {
          return props.isGuestCheckout;
        },
      }),
      email: awEmail(),
    },
    [CONFIRM_EMAIL]: {
      required: awRequired({
        ifFun () {
          return props.isGuestCheckout;
        },
      }),
      email: awEmail(),
    },
    [TAKEOVER_POINT]: {
      required: awRequired(),
    },
    [CONTACT_PHONE]: phoneValidator,
    [COMMENT]: {},
  }, models);

  const validators = computed(() => {
    const result = {};
    [
      FIRST_NAME,
      LAST_NAME,
      EMAIL,
      CONFIRM_EMAIL,
      CONTACT_PHONE,
      COMMENT,
    ].forEach(key => {
      result[key] = awInputV2Props({
        validator: v$.value,
        validatorName: key,
        model: models[key],
        id: `${componentId}_${key}_error`,
      });
    });

    result[TAKEOVER_POINT] = awSelectV2Props({
      validator: v$.value,
      validatorName: TAKEOVER_POINT,
      model: models[TAKEOVER_POINT],
      id: `${componentId}_${TAKEOVER_POINT}_error`,
    });

    return result;
  });

  const isButtonStretch = computed(() => {
    return screenRange.value['mobile-max'];
  });

  const helpTexts = computed(() => {
    return Object.fromEntries(
      ([CONTACT_PHONE]
        .map((k) => {
          return [k, $awt(`aw.checkout.delivery_data_form.help.${camelToSnakeCase(k)}`)];
        })
      ),
    );
  });
  const labels = computed(() => {
    return Object.fromEntries(
      (Object.keys(validators.value)
        .map((k) => {
          return [k, $awt(`aw.checkout.delivery_data_form.label.${camelToSnakeCase(k)}`)];
        })
      ),
    );
  });
  const floatingLabels = computed(() => {
    return screenRange.value['desktop-small-min'] ? {} : labels.value;
  });
  const prefixes = computed(() => {
    return {
      [CONTACT_PHONE]: phonePrefix.value,
    };
  });

  onMounted(() => {
    initForm();
  });

  function initForm () {
    if (!props.isGuestCheckout) {
      initFormWithUserData();
    }
    const customer = currentCheckout.value?.customer;
    const delivery = currentCheckout.value?.delivery;
    for (const key of [FIRST_NAME, LAST_NAME]) {
      if (customer?.[key]) {
        models[key].value = customer[key];
      }
    }
    for (const key of [COMMENT, CONTACT_PHONE]) {
      if (delivery?.address?.[key]) {
        if (key === CONTACT_PHONE) {
          models[key].value = userInterfaceStore.str2Phone(delivery.address[key] || '', true).slice(1).join('');
        } else {
          models[key].value = delivery.address[key];
        }
      }
    }
    for (const key of [TAKEOVER_POINT]) {
      if (delivery?.departmentStore?.id) {
        models[key].value = props.departmentStores?.find(ds => ds?.id === delivery.departmentStore.id);
      }
    }
  }

  function initFormWithUserData () {
    const personalInfo = useUserStore().data.personalInfo;
    models[FIRST_NAME].value = personalInfo.firstName;
    models[LAST_NAME].value = personalInfo.lastName;
    models[CONTACT_PHONE].value = userInterfaceStore.str2Phone(personalInfo.phoneNumber || '', true).slice(1).join('');
    models[EMAIL].value = personalInfo.email;
  }

  async function saveCheckoutDeliveryData (checkoutData) {
    const checkoutStore = useCheckoutStore();
    const deliveryStore = useDeliveryStore();

    const {
      startLoading,
      endLoading,
    } = useUserInterfaceStore();
    const loadingId = { id: `delivery-checkout-data-form-save-${props.formData?.id || 'new'}` };
    try {
      startLoading(loadingId);
      await checkoutStore.updateCheckoutData({
        data: checkoutData,
        type: currentCheckoutType.value,
      });
      await Promise.all([
        deliveryStore.fetchMethod(),
        checkoutStore.fetchCheckout({ type: currentCheckoutType.value }),
      ]);
    } catch (error) {
      $logger.error(error);
    } finally {
      endLoading(loadingId);
    }
  }

  async function onDeliveryDataSaved () {
    const checkoutStore = useCheckoutStore();
    isLoading.value = true;
    const checkoutData = {
      customer: {
        ...(checkoutStore.getCurrentCheckout.customer),
        firstName: models[FIRST_NAME].value,
        lastName: models[LAST_NAME].value,
        ...(props.isGuestCheckout
          ? {
            phoneNumber: models[CONTACT_PHONE].value,
            email: models[EMAIL].value,
            confirmEmail: models[CONFIRM_EMAIL].value,
          }
          : {}
        ),
      },
      delivery: {
        deliveryMethod: props.selectedDeliveryMethod.id,
        departmentStore: models[TAKEOVER_POINT].value,
        address: {
          contactPhone: userInterfaceStore.str2Phone(models[CONTACT_PHONE].value.length ? [phonePrefix.value, models[CONTACT_PHONE].value].join('') : '', true).join(''),
          comment: models[COMMENT].value,
        },
      },
    };
    await saveCheckoutDeliveryData(checkoutData);
    isLoading.value = false;
    emits('take-over-data-saved');
  }
</script>

<style module lang="scss" rel="stylesheet/scss">

.inputList {
  @include font(null, 16px, 24px);
  display: flex;
  flex-direction: column;
  gap: 20px;

  &Accordion {
    gap: 12px;
    padding-top: 20px;

    @include desktop-small(min) {
      gap: 20px;
      padding: 24px 8px 12px;
    }

    .inputListItem {
      --deliveryDataFormLabelWidth: 176px;
    }
  }
}

.inputListItem {
  display: flex;
  flex-direction: column;
  gap: 0 40px;

  @include desktop-small(min) {
    --deliveryDataFormLabelWidth: 176px;
    display: grid;
    grid-template-columns: var(--deliveryDataFormLabelWidth) auto;
    grid-template-areas:
      "labelArea inputArea"
      ".         helpArea"
      ".         errorArea";
  }

  &:last-of-type {
    gap: 0 40px;
  }
}

.inputListItemLabel {
  grid-area: labelArea;
  align-self: center;

  @include tablet(max) {
    display: none;
  }

  &Comment {
    align-self: flex-start;
    padding: 14px 0;
  }
}

.inputListItemInput {
  grid-area: inputArea;

  &Short {
    max-width: 104px;
  }

  &Medium {
    max-width: 200px;
  }

  &Long {
    max-width: 296px;
  }

  &Phone {
    max-width: 152px;
  }

  [data-form-input-select] {
    height: 52px;
  }

  &Select {
    width: 248px;
  }
}

.inputListItemHelp {
  grid-area: helpArea;
  font-style: italic;
  @include font(null, 14px, 20px);
  padding: 4px 16px 0;
  color: $color-text-secondary;

  &:empty {
    display: none;
  }
}

.inputListItemError {
  grid-area: errorArea;
}

.mobileFloatingLabel {
  padding-left: 54px;
}
</style>
